import {
	ComponentProps,
	ReactElement,
	ReactNode,
	cloneElement,
	isValidElement,
	useEffect,
	useRef,
	useState,
} from 'react';
import {
	ToastProvider as LibraryToastProvider,
	type Placement,
	type ToastProps as ToastNotificationProps,
	type TransitionState,
} from 'react-toast-notifications';
import styled from 'styled-components';

import { CircleCheckmarkIcon, CloseIcon, InfoAlternateIcon, WarningIcon } from '../icons';
import { tokens } from '../theme/tokens';

export type ToastAppearance = 'success' | 'warning' | 'error' | 'info';

export type ToastProps = {
	appearance: ToastAppearance;
	title: ReactNode;
	description?: ReactNode;
	action?: ReactNode;
	onDismiss?: () => void;
};

const appearanceTokens = (appearance: ToastAppearance) => tokens[appearance];

const appearanceIcon = (appearance: ToastAppearance) => {
	if (appearance === 'warning' || appearance === 'error') return <WarningIcon size={20} />;
	if (appearance === 'success') return <CircleCheckmarkIcon size={20} />;
	return <InfoAlternateIcon size={20} />;
};

const Shell = styled.div`
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: ${tokens.gap.inline}px;
	width: 380px;
	min-width: 240px;
	max-width: 100%;
	padding: ${tokens.padding.control}px;
	background: ${tokens.surface.white};
	border: 1px solid ${tokens.border.subtle};
	border-radius: ${tokens.radius.md}px;
	box-shadow: ${tokens.shadow.md};
	overflow: hidden;
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	gap: ${tokens.gap.group}px;
	width: 100%;
`;

const Heading = styled.div`
	display: flex;
	flex: 1 1 auto;
	align-items: center;
	gap: ${tokens.gap.element}px;
	min-width: 0;
`;

const IconSlot = styled.div<{ $appearance: ToastAppearance }>`
	flex-shrink: 0;
	width: 20px;
	height: 20px;
	color: ${(props) => appearanceTokens(props.$appearance).icon};

	svg {
		display: block;
		width: 20px;
		height: 20px;
		color: inherit;
	}
`;

const Title = styled.div`
	flex: 1 1 auto;
	min-width: 0;
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.label}px;
	font-weight: ${tokens.typography.weights.medium};
	letter-spacing: 0.01em;
	line-height: ${tokens.typography.lineHeights.heading};
	color: ${tokens.text.primary};
	overflow-wrap: anywhere;
`;

const DismissButton = styled.button`
	display: flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;
	padding: 0;
	border: 0;
	background: transparent;
	color: ${tokens.icon.tertiary};
	cursor: pointer;

	svg {
		display: block;
		width: 16px;
		height: 16px;
		color: inherit;
	}
`;

const Body = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: ${tokens.gap.element}px;
	padding-left: 28px;
	width: 100%;
	box-sizing: border-box;
`;

const Description = styled.div`
	width: 100%;
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.small}px;
	font-weight: 450;
	letter-spacing: 0.01em;
	line-height: ${tokens.typography.lineHeights.small};
	color: ${tokens.text.secondary};
	overflow-wrap: anywhere;
`;

export function Toast({ appearance, title, description, action, onDismiss }: ToastProps) {
	return (
		<Shell role="status">
			<Header>
				<Heading>
					<IconSlot $appearance={appearance}>{appearanceIcon(appearance)}</IconSlot>
					<Title>{title}</Title>
				</Heading>
				{onDismiss && (
					<DismissButton type="button" aria-label="Dismiss" onClick={onDismiss}>
						<CloseIcon size={16} />
					</DismissButton>
				)}
			</Header>
			{(description || action) && (
				<Body>
					{description && <Description>{description}</Description>}
					{action}
				</Body>
			)}
		</Shell>
	);
}

const GUTTER = 8;

function translateForPlacement(placement: Placement) {
	const axis = placement.endsWith('center')
		? placement.startsWith('bottom')
			? 'bottom'
			: 'top'
		: placement.endsWith('right')
			? 'right'
			: 'left';

	if (axis === 'right') return 'translate3d(120%, 0, 0)';
	if (axis === 'left') return 'translate3d(-120%, 0, 0)';
	if (axis === 'bottom') return 'translate3d(0, 120%, 0)';
	return 'translate3d(0, -120%, 0)';
}

function transitionTransform(placement: Placement, state: TransitionState) {
	if (state === 'entering') return translateForPlacement(placement);
	if (state === 'entered') return 'translate3d(0, 0, 0)';
	return 'scale(0.66)';
}

const HeightClip = styled.div<{ $duration: number }>`
	transition: height ${(props) => props.$duration - 100}ms 100ms;
`;

const Motion = styled.div<{
	$placement: Placement;
	$state: TransitionState;
	$duration: number;
}>`
	margin-bottom: ${GUTTER}px;
	max-width: 100%;
	transition:
		transform ${(props) => props.$duration}ms cubic-bezier(0.2, 0, 0, 1),
		opacity ${(props) => props.$duration}ms;
	transform: ${(props) => transitionTransform(props.$placement, props.$state)};
	opacity: ${(props) => (props.$state === 'exiting' || props.$state === 'exited' ? 0 : 1)};
`;

function isToastElement(node: ReactNode): node is ReactElement<ToastProps> {
	return isValidElement(node) && node.type === Toast;
}

function ToastNotification({
	appearance = 'info',
	children,
	onDismiss,
	onMouseEnter,
	onMouseLeave,
	placement,
	transitionDuration,
	transitionState,
}: ToastNotificationProps) {
	const [height, setHeight] = useState<number | 'auto'>('auto');
	const ref = useRef<HTMLDivElement>(null);
	const dismiss = () => onDismiss();

	useEffect(() => {
		if (transitionState === 'entered' && ref.current) {
			setHeight(ref.current.offsetHeight + GUTTER);
		}
		if (transitionState === 'exiting') {
			setHeight(0);
		}
	}, [transitionState]);

	const toast = isToastElement(children) ? (
		cloneElement(children, {
			appearance: children.props.appearance ?? appearance,
			onDismiss: children.props.onDismiss ?? dismiss,
		})
	) : (
		<Toast appearance={appearance} title={children} onDismiss={dismiss} />
	);

	return (
		<HeightClip ref={ref} style={{ height }} $duration={transitionDuration}>
			<Motion
				$placement={placement}
				$state={transitionState}
				$duration={transitionDuration}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
			>
				{toast}
			</Motion>
		</HeightClip>
	);
}

export function ToastProvider({
	placement = 'top-right',
	...props
}: ComponentProps<typeof LibraryToastProvider>) {
	return (
		<LibraryToastProvider
			{...props}
			placement={placement}
			components={{ ...props.components, Toast: ToastNotification }}
		/>
	);
}

export { useToasts } from 'react-toast-notifications';
